import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserContextService } from '../../services/user-context.service';
import { SidebarVisibilityService } from '../../services/sidebar-visibility.service';
import { OfacRecordsService } from '../../services/ofac-records.service';
import { AuditModel } from '../../models/audit.model';

@Component({
  selector: 'app-audit-records',
  templateUrl: './audit-records.component.html',
  styleUrls: ['./audit-records.component.css'],
  standalone: false
})
export class AuditRecordsComponent implements OnInit {
  sourceId: string | null = null;
  auditRecords: AuditModel[] | null = null;
  filteredAuditRecords: AuditModel[] | null = null;
  selectedRecord: AuditModel | null = null;
  isLoading = false;
  errorMessage: string | null = null;
  
  // Sorting state
  sortColumn: string | null = 'createdDate';
  sortDirection: 'asc' | 'desc' = 'desc';
  
  // Search and filter properties
  globalSearch: string = '';
  columnFilters: {
    contactId: string;
    contactName: string;
    ofacMatchTypeName: string;
    applicationName: string;
    createdDate: string;
    createdBy: string;
    confidence: string;
    ofacContactId: string;
    [key: string]: string;
  } = {
    contactId: '',
    contactName: '',
    ofacMatchTypeName: '',
    applicationName: '',
    createdDate: '',
    createdBy: '',
    confidence: '',
    ofacContactId: ''
  };

  constructor(
    private ofacRecordsService: OfacRecordsService,
    private userContext: UserContextService,
    private sidebarVisibilityService: SidebarVisibilityService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    try {
      this.sidebarVisibilityService.showSidebar = true;
      
      // Ensure we have a sourceId, redirect/re-authenticate if needed
      this.sourceId = await this.userContext.ensureSourceIdAsync();

      if (!this.sourceId) {
        // User was redirected or re-authentication failed, don't load data
        return;
      }

      // Load data for the page
      await this.loadData();
    } catch (ex) {
      this.errorMessage = `Error initializing page: ${ex}`;
      // Try to ensure sourceId one more time
      this.sourceId = await this.userContext.ensureSourceIdAsync();
      if (this.sourceId) {
        await this.loadData();
      }
    }
  }

  private async loadData(): Promise<void> {
    if (!this.sourceId) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;
    this.auditRecords = null;

    try {
      this.ofacRecordsService.getAuditsWithApplicationNames(this.sourceId).subscribe({
        next: (records) => {
          this.auditRecords = records;
          this.applyFilters();
          this.applySorting();
          
          // Auto-select first record
          if (this.filteredAuditRecords && this.filteredAuditRecords.length > 0) {
            this.selectedRecord = this.filteredAuditRecords[0];
          }
          this.isLoading = false;
        },
        error: (error) => {
          this.errorMessage = `Error loading data: ${error}`;
          this.isLoading = false;
        }
      });
    } catch (ex) {
      this.errorMessage = `Error loading data: ${ex}`;
      this.isLoading = false;
    }
  }

  onRecordSelected(record: AuditModel): void {
    this.selectedRecord = record;
  }

  navigateToDetail(record: AuditModel): void {
    // Create a composite key from ContactId and OfacContactId
    const contactId = record.contactId || '';
    const ofacContactId = record.ofacContactId?.toString() || '';
    
    // Use base64 encoding to safely pass JSON in URL
    const encodedSearchJson = record.searchJson
      ? btoa(encodeURIComponent(record.searchJson))
      : '';
    const encodedResultJson = record.resultJson
      ? btoa(encodeURIComponent(record.resultJson))
      : '';

    this.router.navigate(['/audit-record-detail'], {
      queryParams: {
        sourceId: this.sourceId,
        contactId: contactId,
        ofacContactId: ofacContactId,
        searchJson: encodedSearchJson,
        resultJson: encodedResultJson
      }
    });
  }

  isRecordSelected(record: AuditModel): boolean {
    if (!this.selectedRecord) {
      return false;
    }

    // Compare by reference first (fastest)
    if (this.selectedRecord === record) {
      return true;
    }

    // If not the same reference, compare by unique fields
    // Use contactId, ofacContactId, createdDate, and applicationName to uniquely identify records
    const selectedDate = this.selectedRecord.createdDate?.getTime() || 0;
    const recordDate = record.createdDate?.getTime() || 0;

    return this.selectedRecord.contactId === record.contactId &&
           this.selectedRecord.ofacContactId === record.ofacContactId &&
           selectedDate === recordDate &&
           this.selectedRecord.applicationName === record.applicationName;
  }

  onGlobalSearchChange(): void {
    this.applyFilters();
  }

  onColumnFilterChange(column: string, value: string): void {
    this.columnFilters[column] = value;
    this.applyFilters();
  }

  clearFilters(): void {
    this.globalSearch = '';
    Object.keys(this.columnFilters).forEach(key => {
      this.columnFilters[key] = '';
    });
    this.applyFilters();
  }

  hasActiveColumnFilters(): boolean {
    return Object.values(this.columnFilters).some(filter => filter.trim() !== '');
  }

  private applyFilters(): void {
    if (!this.auditRecords) {
      this.filteredAuditRecords = null;
      return;
    }

    let filtered = [...this.auditRecords];

    // Apply global search
    if (this.globalSearch.trim()) {
      const searchTerm = this.globalSearch.toLowerCase().trim();
      filtered = filtered.filter(record => this.matchesGlobalSearch(record, searchTerm));
    }

    // Apply column-specific filters
    const filterConfig: { [key: string]: (record: AuditModel, filter: string) => boolean } = {
      contactId: (r, f) => this.matchesFilter(r.contactId?.toLowerCase(), f.toLowerCase()),
      contactName: (r, f) => this.matchesFilter(r.contactName?.toLowerCase(), f.toLowerCase()),
      ofacContactId: (r, f) => this.matchesFilter(r.ofacContactId?.toString(), f),
      ofacMatchTypeName: (r, f) => this.matchesFilter(r.ofacMatchTypeName?.toLowerCase(), f.toLowerCase()),
      applicationName: (r, f) => this.matchesFilter(r.applicationName?.toLowerCase(), f.toLowerCase()),
      createdDate: (r, f) => this.matchesFilter(r.createdDate?.toString(), f),
      createdBy: (r, f) => this.matchesFilter(r.createdBy?.toLowerCase(), f.toLowerCase()),
      confidence: (r, f) => this.matchesFilter(r.confidence?.toLowerCase(), f.toLowerCase())
    };

    Object.keys(this.columnFilters).forEach(key => {
      const filterValue = this.columnFilters[key].trim();
      if (filterValue && filterConfig[key]) {
        filtered = filtered.filter(record => filterConfig[key](record, filterValue));
      }
    });

    this.filteredAuditRecords = filtered;
    
    // Apply sorting after filtering
    this.applySorting();
    
    // Update selected record if it's no longer in filtered results
    this.updateSelectedRecordAfterFilter();
  }

  private matchesGlobalSearch(record: AuditModel, searchTerm: string): boolean {
    return (
      this.matchesFilter(record.contactId?.toLowerCase(), searchTerm) ||
      this.matchesFilter(record.contactName?.toLowerCase(), searchTerm) ||
      this.matchesFilter(record.ofacContactId?.toString(), searchTerm) ||
      this.matchesFilter(record.ofacMatchTypeName?.toLowerCase(), searchTerm) ||
      this.matchesFilter(record.applicationName?.toLowerCase(), searchTerm) ||
      this.matchesFilter(record.createdDate?.toString(), searchTerm) ||
      this.matchesFilter(record.createdBy?.toLowerCase(), searchTerm) ||
      this.matchesFilter(record.confidence?.toLowerCase(), searchTerm)
    );
  }

  private matchesFilter(value: string | undefined, filter: string): boolean {
    return value?.includes(filter) ?? false;
  }

  private updateSelectedRecordAfterFilter(): void {
    if (!this.selectedRecord || !this.filteredAuditRecords) {
      return;
    }

    const stillExists = this.filteredAuditRecords.some(r => 
      r.contactId === this.selectedRecord?.contactId &&
      r.ofacContactId === this.selectedRecord?.ofacContactId
    );

    if (!stillExists) {
      this.selectedRecord = this.filteredAuditRecords.length > 0 
        ? this.filteredAuditRecords[0] 
        : null;
    }
  }

  onSort(event: Event, column: string): void {
    event.stopPropagation();
    if (this.sortColumn === column) {
      // Toggle direction if clicking the same column
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, start with ascending
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  applySorting(): void {
    if (!this.filteredAuditRecords || !this.sortColumn) {
      return;
    }

    this.filteredAuditRecords.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (this.sortColumn) {
        case 'contactId':
          aValue = a.contactId || '';
          bValue = b.contactId || '';
          break;
        case 'contactName':
          aValue = a.contactName || '';
          bValue = b.contactName || '';
          break;
        case 'ofacMatchTypeName':
          aValue = a.ofacMatchTypeName || '';
          bValue = b.ofacMatchTypeName || '';
          break;
        case 'applicationName':
          aValue = a.applicationName || '';
          bValue = b.applicationName || '';
          break;
        case 'createdDate':
          aValue = a.createdDate?.getTime() || 0;
          bValue = b.createdDate?.getTime() || 0;
          break;
        case 'createdBy':
          aValue = a.createdBy || '';
          bValue = b.createdBy || '';
          break;
        case 'confidence':
          aValue = a.confidence || '';
          bValue = b.confidence || '';
          break;
        case 'ofacContactId':
          aValue = a.ofacContactId || 0;
          bValue = b.ofacContactId || 0;
          break;
        default:
          return 0;
      }

      let comparison: number;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return '↕';
    }
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }
}

